export const UK_PROJECTS_ENABLED =
  process.env.NEXT_PUBLIC_UK_PROJECTS_ENABLED === 'true';
import {
  DBS_LIST_COLUMN_LABEL,
  RATE_NOTE_LABEL,
  RATE_TYPE_LABEL,
  RTW_LIST_COLUMN_LABEL,
} from '@lib/constants';

export const TIMEZONE_LOS_ANGELES = 'America/Los_Angeles';
const TIMEZONE_DENVER = 'America/Denver';
const TIMEZONE_CHICAGO = 'America/Chicago';

export const TIMEZONE_NEW_YORK = 'America/New_York';
const TIMEZONE_ANCHORAGE = 'America/Anchorage';
const TIMEZONE_HAWAII = 'Pacific/Honolulu';

export const TIMEZONE_EUROPE_LONDON = 'Europe/London';

type UsTimezoneValue =
  | typeof TIMEZONE_LOS_ANGELES
  | typeof TIMEZONE_DENVER
  | typeof TIMEZONE_CHICAGO
  | typeof TIMEZONE_NEW_YORK
  | typeof TIMEZONE_ANCHORAGE
  | typeof TIMEZONE_HAWAII;

export type TimezoneValue = UsTimezoneValue | typeof TIMEZONE_EUROPE_LONDON;

export const ALLOWED_TIMEZONES: TimezoneValue[] = [
  TIMEZONE_LOS_ANGELES,
  TIMEZONE_DENVER,
  TIMEZONE_CHICAGO,
  TIMEZONE_NEW_YORK,
  TIMEZONE_ANCHORAGE,
  TIMEZONE_HAWAII,
  TIMEZONE_EUROPE_LONDON,
];

export const TIMEZONE_MAP: Record<TimezoneValue, string> = {
  [TIMEZONE_LOS_ANGELES]: 'PST (GMT -8:00)',
  [TIMEZONE_DENVER]: 'MST (GMT -7:00)',
  [TIMEZONE_CHICAGO]: 'CST (GMT -6:00)',
  [TIMEZONE_NEW_YORK]: 'EST (GMT -5:00)',
  [TIMEZONE_ANCHORAGE]: 'AKST (GMT -9:00)',
  [TIMEZONE_EUROPE_LONDON]: 'GMT (GMT +0:00)',
  [TIMEZONE_HAWAII]: 'HST (GMT -10:00)',
};

export const US_TIMEZONE_REGION_MAP: Record<UsTimezoneValue, string> = {
  [TIMEZONE_LOS_ANGELES]: 'Pacific',
  [TIMEZONE_DENVER]: 'Mountain',
  [TIMEZONE_CHICAGO]: 'Central',
  [TIMEZONE_NEW_YORK]: 'Eastern',
  [TIMEZONE_ANCHORAGE]: 'Alaska',
  [TIMEZONE_HAWAII]: 'Hawaii-Aleutian',
};

export const DEFAULT_UNIT_OPTIONS = [
  'Main',
  '2nd Unit',
  'Fitting',
  'Interview',
  'Stills',
];

export const ALL_DEFAULT_UNIT_FORMATS = [
  'Main',
  '2nd',
  '2nd Unit',
  'Fitting',
  'Interview',
  'Stills',
];

export const UNITS_ORDER_MAP = {
  Main: 0,
  '2nd': 1,
  '2nd Unit': 1,
  Fitting: 2,
  Interview: 3,
  Stills: 4,
  Custom: 5,
};

export const BODY_TYPE_OPTIONS = [
  'Average',
  'Slim',
  'Curvy',
  'Athletic/Toned',
  'Muscular',
  'Heavyset/Stocky',
  'Plus sized',
  'Little Person',
];
export const HAIR_LENGTH_OPTIONS = [
  'Bald',
  'Balding',
  'Buzz Cut',
  'Short',
  'Medium',
  'Long',
  'Pixie Cut',
];
export const EYE_COLOR_OPTIONS = [
  'Brown',
  'Blue',
  'Green',
  'Hazel',
  'Amber',
  'Grey',
  'Red',
  'Violet',
];
export const HAIR_COLOR_OPTIONS = [
  'Black',
  'Brown',
  'Light Brown',
  'Blonde',
  'Red',
  'Grey',
  'White',
  'Bald',
  'Other',
];

export const DAYS_OF_THE_WEEK = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const;

export const RATE_TYPE_MAP = {
  'pact/faa': 'PACT/FAA',
  'pact/equity': 'PACT/Equity',
  'bbc/equity': 'BBC/Equity',
  commercial: 'Commercial',
  non_union: 'Non-union',
  unscripted: 'Unscripted',
  other: 'Other',
};

export const DEFAULT_WORK_WEEK = DAYS_OF_THE_WEEK.slice(1, 6);

export type DayOfTheWeek = typeof DAYS_OF_THE_WEEK[number];

export const SORTABLE_LIST_COLUMNS = [
  'Status',
  'Role',
  'Type',
  'First Name',
  'Last Name',
  'Union',
  'Gender',
  'Ethnicity',
  'Age Range',
  'Minors Age',
  'Age',
  'Service',
  'Rate',
  'Notes',
  RATE_TYPE_LABEL,
  RATE_NOTE_LABEL,
  'SR/Van',
  'Height',
  'Weight',
  'Chest',
  'Dress',
  'Waist',
  'Hip',
  'Hat',
  'Neck',
  'Shirt',
  'Sleeve',
  'Pants',
  'Inseam',
  'Shoe',
  'Bra',
  'Suit',
  'Selects',
  'Area',
  'I-9',
  'Dress (UK)',
  'Collar',
  'Chest/Bust',
  'Cup',
  'Natural Waist',
  'Trouser Waist',
  'Hips',
  'Inside Leg',
  'Head',
  'Shoe (UK)',
  RTW_LIST_COLUMN_LABEL,
  DBS_LIST_COLUMN_LABEL,
];

export const UK_LICENSE_TYPES = [
  'Full Car',
  'Advanced Car',
  'Driving Instructor',
  'LGV (HGV) Class 1',
  'LGV (HGV) Class 2',
  'Motorcycle Under 125cc',
  'Motorcycle Above 125cc',
  'PCV (PSV) Coach',
  'PCV (PSV) Minibus',
  'Taxi',
];

export const US_LICENSE_TYPES = [
  "Learner's Permit",
  'Class D',
  'Class M',
  'CDL Class A',
  'CDL Class B',
  'CDL Class C',
  'Taxi/Livery License',
  'Chauffeur’s License',
  'School Bus License',
  'Moped or Scooter License',
  'RV License',
  'Farm Equipment License',
];

export const US_LICENSE_COMPLETE_LABELS = {
  "Learner's Permit": 'Learner’s Permit',
  'Class D': 'Class D (Passenger Car - Regular Driver’s License)',
  'Class M': 'Class M (Motorcycle License)',
  'CDL Class A': 'CDL Class A (Semi-Trucks, Large Trailers)',
  'CDL Class B': 'CDL Class B (Buses, Box Trucks, Dump Trucks)',
  'CDL Class C': 'CDL Class C (HazMat, Passenger Vehicles)',
  'Taxi/Livery License':
    'Taxi/Livery License (For Professional Driving Services)',
  'Chauffeur’s License': 'Chauffeur’s License',
  'School Bus License': 'School Bus License',
  'Moped or Scooter License': 'Moped or Scooter License',
  'RV License': 'RV License (For Large Motorhomes)',
  'Farm Equipment License': 'Farm Equipment License',
};

export const UK_LICENSE_OPTIONS = UK_LICENSE_TYPES.map((license) => ({
  label: license,
  value: license,
}));

export const US_LICENSE_OPTIONS = US_LICENSE_TYPES.map((license) => ({
  label: US_LICENSE_COMPLETE_LABELS[license],
  value: license,
}));

export const POC_DOCUMENT_TYPES = [
  {
    value: 'uk_birth_or_adoption_certificate',
    label: 'UK Birth or Adoption Certificate',
  },
  {
    value: 'irish_birth_or_adoption_certificate',
    label: 'Irish Birth or Adoption Certificate',
  },
  {
    value: 'british_naturalisation_certificate',
    label: 'Certificate of Registration of Naturalisation as a British citizen',
  },
];

export const NO_SELECTION_LABEL = '(No Selection)';

export const UK_COUNTRIES = [
  'England',
  'Ireland',
  'Northern Ireland',
  'Scotland',
  'Wales',
];

export const UK_COUNTRIES_OPTIONS = UK_COUNTRIES.map((country) => ({
  label: country,
  value: country,
}));

export const US_ETHNICITY_MAP = {
  Caucasian: 'Caucasian / White',
  'African American': 'African American / Black',
};

export const CURRENT_YEAR = new Date().getFullYear();

export const CASTING_ROLES = {
  Admin: 'admin',
  Manager: 'manager',
  Casting: 'casting',
  Basic: 'basic',
} as const;

export const isCastingRole = (key: string): key is CastingRoles => {
  return ['admin', 'manager', 'casting', 'basic'].includes(key);
};
export const MANAGE_COMPANY_USERS_ALLOWED_CASTING_ROLES: CastingRoles[] = [
  'admin',
  'manager',
];

type CastingRoleSelectOption = {
  label: string;
  value: CastingRoles;
};

export const castingRoleSelectOptions: CastingRoleSelectOption[] = [
  {
    label: 'Basic',
    value: 'basic',
  },
  {
    label: 'Casting User',
    value: 'casting',
  },
  {
    label: 'Manager',
    value: 'manager',
  },

  {
    label: 'Admin',
    value: 'admin',
  },
];

export const castingRoleSelectOptionByValue: Record<
  CastingRoles,
  CastingRoleSelectOption
> = castingRoleSelectOptions.reduce((acc, roleObj) => {
  return { ...acc, [roleObj.value]: roleObj };
}, {} as Record<CastingRoles, CastingRoleSelectOption>);

export const CASTING_ROLE_LIST: CastingRoles[] = [
  'admin',
  'basic',
  'casting',
  'manager',
];

export const SUB_FOLDER_NAME_FOR_MAIN_LOGO_UPLOAD = 'main-logo-upload';
export const SUB_FOLDER_NAME_FOR_FAVICON_LOGO_UPLOAD = 'favicon-logo-upload';
export const SANDBOX_IDENTIFIER = 'sandbox-casting-saas';
export const DEFAULT_FILE_MAX_SIZE_IN_MB = 5;

export const MAX_TOTAL_USER_SAG_IMAGES = 2;

export const BOOKING_RATE_NOTE_COLUMN_NAME = 'booking_rate_note';
